import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertCircle, Loader2, ArrowRight } from "lucide-react";
import { api } from "../lib/api";

export default function PaymentResult() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", data: null, error: null });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "missing", data: null, error: "No session id" });
      return;
    }
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", data, error: null });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", data, error: null });
          return;
        }
        if (attempts >= 6) {
          setState({ status: "timeout", data, error: null });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setState({
          status: "error",
          data: null,
          error: e?.response?.data?.detail || "Could not verify payment.",
        });
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="bg-stone-950 py-32 min-h-[60vh]" data-testid="payment-result-page">
      <div className="max-w-xl mx-auto px-6 text-center border border-stone-800 bg-stone-900 p-10">
        {state.status === "checking" && (
          <>
            <Loader2 className="h-12 w-12 text-yellow-400 mx-auto animate-spin" />
            <h1 className="mt-6 font-display text-3xl font-bold text-stone-50">
              Verifying your payment…
            </h1>
            <p className="mt-3 text-stone-400">Hang tight, this usually takes a couple seconds.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle2 className="h-14 w-14 text-yellow-400 mx-auto" />
            <h1 className="mt-6 font-display text-4xl font-black text-stone-50 tracking-tight">
              Deposit received.
            </h1>
            <p className="mt-4 text-stone-300 leading-relaxed">
              Your spot is locked in. We'll reach out within one business day
              to confirm the schedule.
            </p>
            <Link
              to="/"
              data-testid="payment-result-home-btn"
              className="mt-8 inline-flex items-center gap-2 bg-yellow-500 text-yellow-950 font-bold uppercase tracking-wider px-6 py-3 border-2 border-yellow-500 hover:bg-yellow-400"
            >
              Back Home <ArrowRight className="h-4 w-4" />
            </Link>
          </>
        )}
        {(state.status === "expired" || state.status === "timeout" || state.status === "error" || state.status === "missing") && (
          <>
            <AlertCircle className="h-12 w-12 text-stone-400 mx-auto" />
            <h1 className="mt-6 font-display text-3xl font-bold text-stone-50">
              {state.status === "expired"
                ? "Session expired"
                : state.status === "error"
                ? "Couldn't verify payment"
                : "Still processing"}
            </h1>
            <p className="mt-3 text-stone-400">
              {state.error || "Your booking is saved. If you were charged, we'll see it shortly. Call us if you want to confirm."}
            </p>
            <Link
              to="/book"
              className="mt-8 inline-flex items-center gap-2 bg-yellow-500 text-yellow-950 font-bold uppercase tracking-wider px-6 py-3 border-2 border-yellow-500 hover:bg-yellow-400"
            >
              Back to Booking
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
